import * as React from 'react';
import { t } from '../../services/HelperService';

const safeString = (v) => (v ?? '').toString();

const cellText = (value) => {
  if (value == null) return '';
  if (typeof value === 'object') {
    try { return JSON.stringify(value); } catch { return safeString(value); }
  }
  return safeString(value);
};

const tableStyle = { borderCollapse: 'collapse', width: '100%', fontSize: 12 };
const cellStyle = { border: '1px solid #dee2e6', padding: '4px 8px', textAlign: 'left', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };

const RowsTable = ({ rows }) => {
  const shown = rows.slice(0, 50);
  const columns = Array.from(new Set(shown.flatMap((r) => (r && typeof r === 'object' ? Object.keys(r) : [])))).slice(0, 12);
  if (columns.length === 0) return null;
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={tableStyle}>
        <thead>
          <tr>{columns.map((c) => <th key={c} style={{ ...cellStyle, background: '#f8f9fa', fontWeight: 700 }}>{c}</th>)}</tr>
        </thead>
        <tbody>
          {shown.map((row, ix) => (
            <tr key={ix}>{columns.map((c) => <td key={c} style={cellStyle} title={cellText(row?.[c])}>{cellText(row?.[c])}</td>)}</tr>
          ))}
        </tbody>
      </table>
      {rows.length > shown.length && (
        <div style={{ fontSize: 11, opacity: 0.6, marginTop: 4 }}>{t('Showing first')} {shown.length} {t('of')} {rows.length} {t('rows')}</div>
      )}
    </div>
  );
};

/**
 * The fallback preview for session profiles that do not bring their own component:
 * generic tabular rendering of each query's latest result rows plus each output's
 * evaluated value. Profile-owned previews (e.g. the ad-hoc report iframe) replace this
 * via the registry's previewComponent (see queryShapingSessionProfiles).
 */
export const DefaultQueryShapingPreview = ({ queries, outputs }) => {
  const queryEntries = (queries ?? []).filter((q) => (q.rows ?? []).length > 0);
  const outputEntries = (outputs ?? []).filter((o) => !!safeString(o.valueJson).trim());

  if (queryEntries.length === 0 && outputEntries.length === 0) {
    return (
      <div style={{ padding: 16, fontSize: 13, opacity: 0.7 }}>
        {t('Nothing to preview yet — test a query or output first (or ask the assistant to).')}
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 16, padding: '4px 2px', overflowY: 'auto', flex: 1, minHeight: 0, alignContent: 'start' }}>
      {queryEntries.map((q) => (
        <div key={q.id}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>{safeString(q.name) || t('Query')}</div>
          <RowsTable rows={q.rows ?? []} />
        </div>
      ))}
      {outputEntries.map((o) => (
        <div key={o.id}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>{safeString(o.name) || t('Output')}</div>
          <pre style={{ margin: 0, padding: '6px 8px', borderRadius: 6, border: '1px solid #dee2e6', background: '#f8f9fa', maxHeight: 260, overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 11 }}>
            {safeString(o.valueJson)}
          </pre>
        </div>
      ))}
    </div>
  );
};
